import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  Observable,
  catchError,
  finalize,
  map,
  of,
  share,
  shareReplay,
  switchMap,
  tap,
  throwError,
  timer,
} from 'rxjs';

import { environment } from '../../../environments/environment';
import type {
  AuthTokensResponse,
  AuthUser,
  LogoutRequestBody,
  RefreshRequestBody,
  SwitchClientRequestBody,
  SwitchClientResponse,
} from '../models/auth-api.model';
import type { JwtAccessClaims } from '../models/jwt-claims.model';
import { TokenStorageService } from './token-storage.service';
import { normalizeSwitchClientResponse } from '../utils/auth-response.util';
import {
  decodeJwtPayload,
  jwtNumericClaim,
  looksLikeJwt,
  secondsUntilJwtExpiry,
} from '../utils/jwt-decode.util';
import { normalizeApiBase } from '../utils/auth-http.util';

/**
 * Auth against Abram Pro (or compatible) API: login, rotating refresh, switch-client, logout.
 * Exposes tokens only via snapshots for interceptors — do not log token values in production.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  /** Skips interceptor chain — refresh must not re-enter the 401 handler. */
  private readonly rawHttp = new HttpClient(inject(HttpBackend));
  private readonly tokens = inject(TokenStorageService);
  private readonly router = inject(Router);

  private readonly _accessToken = signal<string | null>(null);
  private readonly _user = signal<AuthUser | null>(null);

  private static readonly REFRESH_LOCK = 'aps_auth_refresh_lock';
  private static readonly REFRESH_LOCK_TTL_MS = 15_000;

  private refreshInFlight$: Observable<AuthTokensResponse> | null = null;
  private switchClientInFlight$: Observable<SwitchClientResponse> | null = null;
  private restoreClientInFlight$: Observable<void> | null = null;
  /** One-shot bootstrap: proactive refresh when access token is near expiry. */
  private sessionReady$: Observable<void> | null = null;
  private proactiveTimer: ReturnType<typeof setTimeout> | null = null;

  readonly accessToken = this._accessToken.asReadonly();
  readonly user = this._user.asReadonly();

  readonly activeClientId = computed((): string | null => {
    const t = this._accessToken();
    if (!t) {
      return null;
    }
    return decodeJwtPayload<JwtAccessClaims>(t)?.activeClientId ?? null;
  });

  /** Alias for feature services that call `/api/clients/{clientId}/…`. */
  readonly clientId = this.activeClientId;

  readonly allowedClients = computed((): string[] => {
    const t = this._accessToken();
    if (!t) {
      return [];
    }
    return decodeJwtPayload<JwtAccessClaims>(t)?.allowedClients ?? [];
  });

  readonly isAuthenticated = computed(
    () => !!this._accessToken() && !!this.activeClientId(),
  );

  readonly isAdmin = computed(() => this._user()?.role === 'admin');

  readonly isClientView = computed(() => this._user()?.role === 'client_view');

  readonly isStaff = computed(() => {
    const role = this._user()?.role;
    return role === 'admin' || role === 'sales_rep';
  });

  /** True when the signed-in user may create, edit, or deactivate clients. */
  readonly canWriteClients = computed(() => this.isAdmin());

  constructor() {
    this.hydrateFromStorage();
    this.bindCrossTabStorageSync();
    if (this._accessToken() || this.hasRefreshToken()) {
      this.ensureSessionReady().subscribe({
        error: () => {
          /* non-fatal; JWT active client remains */
        },
      });
    }
  }

  /**
   * Ensures the access token is refreshed when needed before guarded routes load.
   * Refresh keeps `activeClientId` from the rotated refresh token (no extra switch-client).
   */
  ensureSessionReady(): Observable<void> {
    if (!this.sessionReady$) {
      this.sessionReady$ = this.runSessionBootstrap().pipe(
        tap(() => this.scheduleProactiveRefreshFromStoredExpiry()),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.sessionReady$;
  }

  private apiBase(): string {
    return normalizeApiBase(environment.apiUrl);
  }

  /** Sync signals from Session / Local storage (app reload). */
  hydrateFromStorage(): void {
    const at = this.tokens.getAccessToken();
    this._accessToken.set(at);
    const uj = this.tokens.getUserJson();
    if (uj) {
      try {
        this._user.set(JSON.parse(uj) as AuthUser);
      } catch {
        this._user.set(null);
      }
    } else {
      this._user.set(null);
    }
  }

  /** Bearer token for HTTP — prefers persisted storage so the exact JWT from login/switch is sent. */
  getAccessTokenSnapshot(): string | null {
    return this.tokens.getAccessToken() ?? this._accessToken();
  }

  hasRefreshToken(): boolean {
    return !!this.tokens.getRefreshToken();
  }

  /** True when access expiry (stored or JWT `exp`) is within buffer. */
  isAccessExpired(bufferMs = 10_000): boolean {
    const exp = this.tokens.getAccessExpiresAtMs();
    if (exp) {
      return Date.now() + bufferMs >= exp;
    }
    const t = this._accessToken();
    if (!t) {
      return true;
    }
    const sec = secondsUntilJwtExpiry(t);
    if (sec == null) {
      return false;
    }
    return sec * 1000 < bufferMs;
  }

  /** Used by route guard when access is expired but refresh may still work. */
  hydrateAccessToken(): Observable<boolean> {
    if (!this.hasRefreshToken()) {
      return throwError(() => new Error('No refresh token'));
    }
    return this.ensureSessionReady().pipe(map(() => this.isAuthenticated()));
  }

  login(email: string, password: string, rememberMe: boolean): Observable<void> {
    return this.http
      .post<AuthTokensResponse>(`${this.apiBase()}/api/auth/login`, { email, password })
      .pipe(
        tap((res) => {
          this.sessionReady$ = null;
          this.applyFullTokenResponse(res, rememberMe);
        }),
        switchMap(() => this.restorePreferredClientIfNeeded()),
        map(() => void 0),
      );
  }

  /**
   * Rotates refresh token; updates storage. Uses HttpClient without interceptor loop via raw client.
   */
  performTokenRefresh(): Observable<AuthTokensResponse> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }
    const refreshToken = this.tokens.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token'));
    }
    const url = `${this.apiBase()}/api/auth/refresh`;
    const rememberMe = this.tokens.getActiveMedium() === 'local';
    this.refreshInFlight$ = this.whenSwitchIdle().pipe(
      switchMap(() => {
        if (!this.tryAcquireRefreshLock()) {
          return this.awaitPeerRefresh(url, refreshToken);
        }
        return this.rawHttp
          .post<AuthTokensResponse>(url, { refreshToken } satisfies RefreshRequestBody)
          .pipe(finalize(() => this.releaseRefreshLock()));
      }),
      tap((res) => this.applyRefreshTokenResponse(res, rememberMe)),
      finalize(() => {
        this.refreshInFlight$ = null;
      }),
      share(),
    );
    return this.refreshInFlight$;
  }

  switchClient(clientId: string): Observable<SwitchClientResponse> {
    const trimmed = clientId.trim();
    const run$ = this.executeSwitchClient(trimmed);
    if (this.switchClientInFlight$) {
      return this.switchClientInFlight$.pipe(switchMap(() => run$));
    }
    this.switchClientInFlight$ = run$.pipe(
      finalize(() => {
        this.switchClientInFlight$ = null;
      }),
      share(),
    );
    return this.switchClientInFlight$;
  }

  private executeSwitchClient(clientId: string): Observable<SwitchClientResponse> {
    return this.whenRefreshIdle().pipe(
      switchMap(() => {
        const bearer = this.getAccessTokenSnapshot();
        if (!bearer || !looksLikeJwt(bearer)) {
          return throwError(
            () => new Error('Session expired or access token is invalid — sign in again.'),
          );
        }
        return this.http
          .post<unknown>(`${this.apiBase()}/api/auth/switch-client`, {
            clientId,
          } satisfies SwitchClientRequestBody)
          .pipe(
            map((body) => normalizeSwitchClientResponse(body)),
            tap((res) => this.applySwitchClientResponse(res, clientId)),
          );
      }),
    );
  }

  private applySwitchClientResponse(res: SwitchClientResponse, clientId: string): void {
    const accessToken = res.accessToken;
    if (!looksLikeJwt(accessToken)) {
      throw new Error('Switch-client returned an invalid access token.');
    }
    const expSec = jwtNumericClaim(decodeJwtPayload<{ exp?: unknown }>(accessToken)?.exp);
    const accessExpiresAtMs =
      expSec != null ? expSec * 1000 : Date.now() + res.expiresIn * 1000;
    this.tokens.updateAccessOnly(accessToken, accessExpiresAtMs);
    this._accessToken.set(accessToken);
    this.scheduleProactiveRefresh(res.expiresIn);
    const userId = this._user()?.id;
    if (userId) {
      this.tokens.setPreferredWorkspaceClient(userId, clientId);
    }
  }

  logout(): Observable<void> {
    const url = `${this.apiBase()}/api/auth/logout`;
    const access = this._accessToken();
    const refresh = this.tokens.getRefreshToken();
    if (!access) {
      this.clearLocalSession();
      return of(void 0);
    }
    const body: LogoutRequestBody = refresh ? { refreshToken: refresh } : {};
    return this.http.post(url, body).pipe(
      tap(() => this.clearLocalSession()),
      catchError(() => {
        this.clearLocalSession();
        return of(void 0);
      }),
      map(() => void 0),
    );
  }

  clearLocalSession(): void {
    if (this.proactiveTimer) {
      clearTimeout(this.proactiveTimer);
      this.proactiveTimer = null;
    }
    this.sessionReady$ = null;
    this.restoreClientInFlight$ = null;
    this.tokens.clearBoth();
    this._accessToken.set(null);
    this._user.set(null);
  }

  clearSessionAndRedirect(router: Router): void {
    this.clearLocalSession();
    void router.navigate(['/login'], { replaceUrl: true });
  }

  private applyFullTokenResponse(res: AuthTokensResponse, rememberMe: boolean): void {
    const accessExpiresAtMs = Date.now() + res.expiresIn * 1000;
    this.tokens.setSessionData({
      accessToken: res.accessToken,
      refreshToken: res.refreshToken,
      accessExpiresAtMs,
      userJson: JSON.stringify(res.user),
      rememberMe,
    });
    this._accessToken.set(res.accessToken);
    this._user.set(res.user);
    this.scheduleProactiveRefresh(res.expiresIn);
  }

  /** Token rotation after `/auth/refresh` — updates storage in place (no clear-then-set). */
  private applyRefreshTokenResponse(res: AuthTokensResponse, rememberMe: boolean): void {
    const accessExpiresAtMs = Date.now() + res.expiresIn * 1000;
    this.tokens.updateSessionTokens({
      accessToken: res.accessToken,
      refreshToken: res.refreshToken,
      accessExpiresAtMs,
      userJson: JSON.stringify(res.user),
      rememberMe,
    });
    this._accessToken.set(res.accessToken);
    this._user.set(res.user);
    this.scheduleProactiveRefresh(res.expiresIn);
  }

  /**
   * After login the API issues the first allowed client; re-applies the last workspace
   * choice from localStorage via switch-client when it differs.
   */
  restorePreferredClientIfNeeded(): Observable<void> {
    if (this.restoreClientInFlight$) {
      return this.restoreClientInFlight$;
    }

    const userId = this._user()?.id;
    const preferred = userId ? this.tokens.getPreferredWorkspaceClient(userId) : null;
    const active = this.activeClientId();

    if (!preferred || preferred === active) {
      return of(void 0);
    }

    const allowed = new Set(this.allowedClients());
    if (!allowed.has(preferred)) {
      this.tokens.clearPreferredWorkspaceClient();
      return of(void 0);
    }

    this.restoreClientInFlight$ = this.switchClient(preferred).pipe(
      map(() => void 0),
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && (err.status === 403 || err.status === 404)) {
          this.tokens.clearPreferredWorkspaceClient();
        }
        return of(void 0);
      }),
      finalize(() => {
        this.restoreClientInFlight$ = null;
      }),
      share(),
    );
    return this.restoreClientInFlight$;
  }

  private runSessionBootstrap(): Observable<void> {
    const hasAccess = !!this.getAccessTokenSnapshot();
    const hasRefresh = this.hasRefreshToken();

    if (!hasAccess && !hasRefresh) {
      return of(void 0);
    }

    /* New tab: refresh token in localStorage but access only in another tab's sessionStorage. */
    if (!hasAccess && hasRefresh) {
      return this.refreshOrPropagateFailure();
    }

    if (!hasRefresh || !this.isAccessExpired(30_000)) {
      return of(void 0);
    }

    return this.refreshOrPropagateFailure();
  }

  private refreshOrPropagateFailure(): Observable<void> {
    return this.performTokenRefresh().pipe(
      map(() => void 0),
      catchError((err: unknown) => {
        if (!this.getAccessTokenSnapshot()) {
          return throwError(() => err);
        }
        return of(void 0);
      }),
    );
  }

  /** Sync login/logout and token rotation across browser tabs. */
  private bindCrossTabStorageSync(): void {
    const win = typeof globalThis !== 'undefined' ? globalThis.window : undefined;
    if (!win) {
      return;
    }
    win.addEventListener('storage', (ev: StorageEvent) => {
      if (ev.storageArea !== win.localStorage) {
        return;
      }

      const key = ev.key;
      const syncKeys = new Set<string | null>([
        TokenStorageService.REFRESH,
        TokenStorageService.MEDIUM,
        TokenStorageService.ACCESS,
        TokenStorageService.ACCESS_EXP,
        TokenStorageService.USER,
      ]);
      if (!syncKeys.has(key)) {
        return;
      }

      if (!this.tokens.getRefreshToken()) {
        this.clearSessionAndRedirect(this.router);
        return;
      }

      this.hydrateFromStorage();

      if (!this.getAccessTokenSnapshot()) {
        this.sessionReady$ = null;
        this.ensureSessionReady().subscribe({
          error: () => {
            /* ignore */
          },
        });
        return;
      }

      this.scheduleProactiveRefreshFromStoredExpiry();
    });
  }

  private tryAcquireRefreshLock(): boolean {
    const win = typeof globalThis !== 'undefined' ? globalThis.window : undefined;
    if (!win) {
      return true;
    }
    const now = Date.now();
    try {
      const raw = win.localStorage.getItem(AuthService.REFRESH_LOCK);
      if (raw) {
        const ts = Number(raw);
        if (Number.isFinite(ts) && now - ts < AuthService.REFRESH_LOCK_TTL_MS) {
          return false;
        }
      }
      win.localStorage.setItem(AuthService.REFRESH_LOCK, String(now));
      return true;
    } catch {
      return true;
    }
  }

  private releaseRefreshLock(): void {
    try {
      globalThis.window?.localStorage.removeItem(AuthService.REFRESH_LOCK);
    } catch {
      /* ignore */
    }
  }

  /**
   * Another tab holds the refresh lock — wait for rotated tokens, then reuse them or
   * refresh once with the updated refresh token for this tab's session storage.
   */
  private awaitPeerRefresh(
    url: string,
    refreshToken: string,
  ): Observable<AuthTokensResponse> {
    return timer(1_500).pipe(
      tap(() => this.hydrateFromStorage()),
      switchMap(() => {
        const snapshot = this.tokensSnapshotFromStorage();
        if (snapshot && !this.isAccessExpired(5_000)) {
          return of(snapshot);
        }
        const rt = this.tokens.getRefreshToken() ?? refreshToken;
        if (!this.tryAcquireRefreshLock()) {
          return throwError(() => new Error('Refresh lock still held'));
        }
        return this.rawHttp
          .post<AuthTokensResponse>(url, { refreshToken: rt } satisfies RefreshRequestBody)
          .pipe(finalize(() => this.releaseRefreshLock()));
      }),
    );
  }

  private tokensSnapshotFromStorage(): AuthTokensResponse | null {
    const accessToken = this.getAccessTokenSnapshot();
    const refreshToken = this.tokens.getRefreshToken();
    const user = this._user();
    if (!accessToken || !refreshToken || !user) {
      return null;
    }
    const exp = this.tokens.getAccessExpiresAtMs();
    const expiresIn =
      exp != null ? Math.max(1, Math.floor((exp - Date.now()) / 1000)) : 900;
    return { accessToken, refreshToken, expiresIn, refreshExpiresIn: 0, user };
  }

  private whenRefreshIdle(): Observable<void> {
    if (!this.refreshInFlight$) {
      return of(void 0);
    }
    return this.refreshInFlight$.pipe(
      map(() => void 0),
      catchError(() => of(void 0)),
    );
  }

  private whenSwitchIdle(): Observable<void> {
    if (!this.switchClientInFlight$) {
      return of(void 0);
    }
    return this.switchClientInFlight$.pipe(
      map(() => void 0),
      catchError(() => of(void 0)),
    );
  }

  private scheduleProactiveRefreshFromStoredExpiry(): void {
    const exp = this.tokens.getAccessExpiresAtMs();
    if (!exp || !this._accessToken()) {
      return;
    }
    const secLeft = Math.max(0, Math.floor((exp - Date.now()) / 1000));
    if (secLeft > 90) {
      this.scheduleProactiveRefresh(secLeft);
    } else if (this.hasRefreshToken()) {
      this.performTokenRefresh().subscribe({
        error: () => {
          /* handled on next 401 or navigation */
        },
      });
    }
  }

  /** Refresh ~60s before access expiry; minimum delay clamp avoids tight loops. */
  private scheduleProactiveRefresh(expiresInSec: number): void {
    if (this.proactiveTimer) {
      clearTimeout(this.proactiveTimer);
      this.proactiveTimer = null;
    }
    const skew = 60;
    const ms = Math.max((expiresInSec - skew) * 1000, 15_000);
    this.proactiveTimer = setTimeout(() => {
      if (!this.hasRefreshToken()) {
        return;
      }
      this.performTokenRefresh().subscribe({
        error: (_err: unknown) => {
          if (_err instanceof HttpErrorResponse && _err.status === 401) {
            /* invalid refresh — next request will clear session via interceptor */
          }
        },
      });
    }, ms);
  }
}
